import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { ProfessionalReportService, ProfessionalData, ReportField, ProfessionalsResponse } from '../../shared/services/professional-report.service';

@Component({
  selector: 'app-professional-report-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDividerModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatProgressBarModule,
  ],
  templateUrl: './professional-report-builder.component.html',
  styleUrls: ['./professional-report-builder.component.scss']
})
export class ProfessionalReportBuilderComponent implements OnInit {
  professionals: ProfessionalData[] = [];
  availableFields: ReportField[] = [];
  displayedColumns: string[] = [];

  selectedStatus = 'ALL';
  selectedIsActive = 'ALL';
  selectedEmploymentType = 'ALL';
  fieldSearch = '';
  loading = false;
  totalProfessionals = 0;

  statusOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'REJECTED', label: 'Rejeitado' }
  ];

  isActiveOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: 'true', label: 'Ativo' },
    { value: 'false', label: 'Inativo' },
  ];

  employmentTypeOptions = [
    { value: 'ALL', label: 'Todos' },
    { value: 'service_provider', label: 'Prestador de Serviço' },
    { value: 'partner', label: 'Sócio' },
    { value: 'employee', label: 'CLT' },
  ];

  constructor(
    private professionalReportService: ProfessionalReportService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.initializeFields();
    this.loadProfessionals();
  }

  initializeFields() {
    this.availableFields = this.professionalReportService.getAvailableFields();
    this.updateDisplayedColumns();
  }

  updateDisplayedColumns() {
    this.displayedColumns = this.availableFields
      .filter(field => field.selected)
      .map(field => field.key);
  }

  get filteredFields(): ReportField[] {
    const term = (this.fieldSearch || '').trim().toLowerCase();
    if (!term) return this.availableFields;

    return this.availableFields.filter((field) => {
      const hay = `${field.header} ${field.key}`.toLowerCase();
      return hay.includes(term);
    });
  }

  loadProfessionals() {
    this.loading = true;
    const status = this.selectedStatus === 'ALL' ? undefined : this.selectedStatus;
    const isActive = this.selectedIsActive === 'ALL' ? undefined : this.selectedIsActive;
    const employmentType = this.selectedEmploymentType === 'ALL' ? undefined : this.selectedEmploymentType;

    this.professionalReportService.getProfessionalsData(status, isActive, employmentType).subscribe({
      next: (response: ProfessionalsResponse) => {
        this.professionals = response.professionals;
        this.totalProfessionals = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar profissionais:', error);
        this.snackBar.open('Erro ao carregar dados dos profissionais', 'Fechar', {
          duration: 3000
        });
        this.loading = false;
      }
    });
  }

  onStatusChange() {
    this.loadProfessionals();
  }

  onIsActiveChange() {
    this.loadProfessionals();
  }

  onEmploymentTypeChange() {
    this.loadProfessionals();
  }

  onFieldSelectionChange() {
    this.updateDisplayedColumns();
  }

  selectAllFields() {
    this.availableFields.forEach(field => field.selected = true);
    this.updateDisplayedColumns();
  }

  deselectAllFields() {
    this.availableFields.forEach(field => field.selected = false);
    this.updateDisplayedColumns();
  }

  getSelectedFieldsCount(): number {
    return this.availableFields.filter(field => field.selected).length;
  }

  getSelectedFieldsCountFiltered(): number {
    return this.filteredFields.filter((field) => field.selected).length;
  }

  exportToExcel() {
    const selectedFields = this.availableFields
      .filter(field => field.selected)
      .map(field => field.key);

    if (selectedFields.length === 0) {
      this.snackBar.open('Selecione pelo menos um campo para exportar', 'Fechar', {
        duration: 3000
      });
      return;
    }

    this.loading = true;
    const filters = {
      status: this.selectedStatus === 'ALL' ? undefined : this.selectedStatus,
      isActive: this.selectedIsActive === 'ALL' ? undefined : this.selectedIsActive,
      employmentType: this.selectedEmploymentType === 'ALL' ? undefined : this.selectedEmploymentType,
    };

    this.professionalReportService.generateCustomSpreadsheet(selectedFields, filters).subscribe({
      next: () => {
        this.snackBar.open('Relatório exportado com sucesso!', 'Fechar', {
          duration: 3000
        });
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao exportar relatório:', error);
        this.snackBar.open('Erro ao exportar relatório', 'Fechar', {
          duration: 3000
        });
        this.loading = false;
      }
    });
  }

  getFieldHeader(fieldKey: string): string {
    const field = this.availableFields.find(f => f.key === fieldKey);
    return field ? field.header : fieldKey;
  }

  getValueForField(professional: ProfessionalData, fieldKey: string): any {
    return (professional as any)[fieldKey] || '';
  }

  getSelectedStatusLabel(): string {
    const option = this.statusOptions.find(opt => opt.value === this.selectedStatus);
    return option ? option.label : 'Todos';
  }
}
